import React from 'react';
import * as _ from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n-lite';
import { bindActionCreators } from 'redux';
import { v4 as uuid } from 'uuid';
import { SmartButton } from '../../common/SmartButton';

import * as bookActions from '../../../actions/bookActions';
import * as schoolActions from '../../../actions/schoolActions';
import * as drawerActions from '../../../actions/drawerActions';
import * as accountActions from '../../../actions/accountActions';
import * as classroomActions from '../../../actions/classroom/classroomActions';
import DragAndDropContainer from '../../common/DragAndDropContainer';
import SadMonster from '../../common/EmptyListPlaceholder/SadMonster';
import NewContentCard from '../NewContentCard';
import { DRAWER_CONTENTS, ROLE } from '../../../const';
import BookCardPlaceholder from '../BookCardPlaceholder';
import { BookCard } from '@netexknowledge/smart-components';
import { ROUTES } from '../../../const';
import * as optionItemsFactory from '../../../factory/optionItemsFactory';
import routerHistory from './../../../routerHistory';
import { ANALYTICS_CONTEXT } from '../../../types/analytics';
import Media from 'react-media';
import { MOBILE_MEDIA_QUERY } from '../../../utils/mobileUtils';
import MoreOptions from '../../common/sheets/MoreOptions';

export class MyContent extends React.Component {
  handleBookVisibility = item => {
    const { isAppOnline } = this.props;

    if (isAppOnline) {
      const { bookActions, classroom } = this.props;

      let activityId = item.activityId;
      let bookId = item.id;

      if (item.disabled) {
        bookActions.enableBookLevel(classroom.id, bookId, activityId, ANALYTICS_CONTEXT.BOOK_SHOW);
      } else {
        bookActions.disableBookLevel(classroom.id, bookId, activityId, ANALYTICS_CONTEXT.BOOK_HIDE);
      }
    }
  };

  handleRemoveBook = item => {
    const { isAppOnline } = this.props;

    if (isAppOnline) {
      const { bookActions } = this.props;
      bookActions.setRemoveBookState(item);
    }
  };

  handleCopyBook = item => {
    const { isAppOnline } = this.props;

    if (isAppOnline) {
      const { bookActions } = this.props;
      let copyReveal = { activityId: item.activityId };
      bookActions.setCopyRevealData(copyReveal);
    }
  };

  getTeacherOptions = item => {
    const { authoring, importContentInClassroom } = this.props;
    let visibilityText = item.disabled ? I18n.t('common.option.show') : I18n.t('common.option.hide');
    let visibilityIcon = item.disabled ? 'custom-icons-eye-view' : 'custom-icons-no-eye-view';

    let options = [];

    if (authoring && importContentInClassroom) {
      options.push(
        optionItemsFactory.getNewVisibilityItem(visibilityText, visibilityIcon, () => {
          this.handleBookVisibility(item);
        })
      );
      options.push(
        optionItemsFactory.getNewDeleteItem(() => {
          this.handleRemoveBook(item);
        })
      );
      options.push(
        optionItemsFactory.getNewCopyItem(() => {
          this.handleCopyBook(item);
        })
      );
    }
    return options;
  };

  getOptions = item => {
    const { role, extraMenuOptions } = this.props;

    const isInstructor = role === ROLE.INSTRUCTOR;
    let optionItems = isInstructor ? this.getTeacherOptions(item) : [];
    if (extraMenuOptions) {
      extraMenuOptions.forEach(o => {
        optionItems.splice(o.position, 0, o.option);
      });
    }
    return optionItems;
  };

  componentDidMount = () => {
    const { classroom, bookActions } = this.props;

    if (classroom && classroom.id) {
      let classroomId = classroom.id;
      bookActions.getBooksData(classroomId);
    }
  };

  componentDidUpdate = prevProps => {
    if (this.props.classroom.id !== prevProps.classroom.id) {
      const { classroom, bookActions } = this.props;
      let classroomId = classroom.id;
      bookActions.getBooksData(classroomId);
    }
  };

  isTeacher = () => {
    const { role } = this.props;
    return role === ROLE.INSTRUCTOR;
  };

  renderStaticCard = () => {
    return (
      this.isTeacher() && (
        <NewContentCard
          customMessage={I18n.t('common.newContent')}
          customCss="book-card-new"
          onClick={this.createCustomBook}
        />
      )
    );
  };

  renderCard = card => {
    let { item } = card;
    const { classroom } = this.props;

    if (classroom && classroom.id) {
      const options = this.getOptions(item);
      return (
        <Media query={MOBILE_MEDIA_QUERY}>
          {isMobile => {
            // Mobile: the card's own dropdown is replaced by a ⋮ that opens the options in a bottom sheet
            const sheetOptions = isMobile && options.length > 0;
            return (
              <div
                className={[
                  'book-card-component',
                  item.disabled ? 'hide-book' : '',
                  sheetOptions ? 'book-card-component--sheet-options' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <BookCard
                  id={item.id}
                  title={item.bookName}
                  subtitle={item.metadata.map(meta => `#${meta.metadataValue} `)}
                  picture={item.previewImage || item.image}
                  onClickCard={bookId => {
                    routerHistory.push(`${ROUTES.CLASSROOM}/${classroom.id}/book/${bookId}`);
                  }}
                  catalogMode={false}
                  options={sheetOptions ? [] : options}
                />
                {sheetOptions && (
                  <div className="book-card-component__options">
                    <MoreOptions
                      id={`book-options-${item.id}`}
                      color="white"
                      options={options}
                      sheetTitle={item.bookName}
                      sheetOverline={[
                        I18n.t('mobileSheets.book'),
                        ...item.metadata.map(meta => `#${meta.metadataValue}`)
                      ].join(' ')}
                      sheetMedia={
                        <span
                          className="sheet-media-image"
                          style={
                            item.previewImage || item.image
                              ? { backgroundImage: `url(${item.previewImage || item.image})` }
                              : undefined
                          }
                        />
                      }
                    />
                  </div>
                )}
              </div>
            );
          }}
        </Media>
      );
    }
  };

  renderEmptyClassroom = () => {
    let addContentButton;

    if (this.isTeacher()) {
      addContentButton = (
        <SmartButton disabled={false} onClick={this.createCustomBook} success>
          {I18n.t('common.newContent')}
        </SmartButton>
      );
    }

    return (
      <div className="empty-classroom">
        <SadMonster msgKey="classroom.noBooksFound" />
        {addContentButton}
      </div>
    );
  };

  getCardDimensions = () => {
    return {
      width: 265,
      height: 265
    };
  };

  renderBooks = () => {
    const { books, authoring, importContentInClassroom } = this.props;

    if (!books) {
      return (
        <div className="books-container">
          <BookCardPlaceholder />
        </div>
      );
    }

    let renderStaticCard;

    if (this.isTeacher()) {
      renderStaticCard = this.renderStaticCard;
    }
    if (_.isEmpty(books)) {
      return this.renderEmptyClassroom();
    }
    return (
      <DragAndDropContainer
        cardId="id"
        cards={books}
        rotation={5}
        containerClassName="books-container"
        renderCard={this.renderCard}
        renderStaticCard={authoring && importContentInClassroom ? renderStaticCard : undefined}
        dragActivated={false}
        id="classroomActiveContainer"
        cardDimensions={this.getCardDimensions}
      />
    );
  };

  createCustomBook = e => {
    const { drawerActions, isAppOnline } = this.props;

    e.preventDefault();
    if (isAppOnline) {
      drawerActions.openDrawerAndSetContent(DRAWER_CONTENTS.CONTENT_CREATE);
    }
  };

  render() {
    return (
      <div key="mycontent" style={{ height: 'calc(100% - 16px)' }}>
        {this.renderBooks()}
      </div>
    );
  }
}

MyContent.propTypes = {
  classroom: PropTypes.any,
  bookActions: PropTypes.any,
  user: PropTypes.any,
  role: PropTypes.string,
  btnSuccess: PropTypes.any,
  btnSuccessHover: PropTypes.any,
  books: PropTypes.any,
  authoring: PropTypes.any,
  isAppOnline: PropTypes.any,
  drawerActions: PropTypes.any,
  importContentInClassroom: PropTypes.bool
};

function mapStateToProps(state) {
  return {
    user: state.account.user,
    role: state.classrooms.selectedClassroom.classroom.role,
    books: state.book.books,
    classroom: state.classrooms.selectedClassroom.classroom,
    isAppOnline: state.connection.status.online,
    colors: state.theme.colors,
    layoutSettings: state.theme.layoutSettings,
    authoring: state.account.configuration.authoring,
    importContentInClassroom: state.account.configuration.importContentInClassroom,
    btnSuccess: state.theme.colors.btnSuccess,
    btnSuccessHover: state.theme.colors.btnSuccessHover
  };
}

function mapDispatchToProps(dispatch) {
  return {
    accountActions: bindActionCreators(accountActions, dispatch),
    bookActions: bindActionCreators(bookActions, dispatch),
    drawerActions: bindActionCreators(drawerActions, dispatch),
    classroomActions: bindActionCreators(classroomActions, dispatch),
    schoolActions: bindActionCreators(schoolActions, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(MyContent);
